import { isBefore } from "date-fns";
import { ProspectSchoolDTO } from "../types/Prospect";
import { SchoolAttendingStatus, SchoolTypeName } from "../types/School";

export const isFuture = (school: ProspectSchoolDTO): boolean =>
  school.schoolAttendingStatusId === SchoolAttendingStatus.SIGNED_NLI ||
  school.schoolAttendingStatusId === SchoolAttendingStatus.VERBAL_COMMIT;

export const isCurrent = (school: ProspectSchoolDTO): boolean =>
  school.schoolAttendingStatusId === SchoolAttendingStatus.ATTENDING;

export const isPrevious = (school: ProspectSchoolDTO): boolean =>
  school.schoolAttendingStatusId === SchoolAttendingStatus.ATTENDED ||
  school.schoolAttendingStatusId === SchoolAttendingStatus.GRADUATED;

export const isCollege = (school: ProspectSchoolDTO): boolean =>
  !!school.schoolTypeName &&
  (school.schoolTypeName === SchoolTypeName.FOUR_YEAR_COLLEGE ||
    school.schoolTypeName === SchoolTypeName.JUNIOR_COLLEGE);

export const isHighSchool = (school: ProspectSchoolDTO): boolean =>
  !!school.schoolTypeName && school.schoolTypeName === SchoolTypeName.HIGH_SCHOOL;

export const validStartDate = (school: ProspectSchoolDTO): boolean => {
  const startDate = !!school.startDate ? new Date(school.startDate) : null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const future = isFuture(school);
  // future school start date must be in future else start date must be in past
  return (future && !!startDate && startDate >= now) || (!future && !!startDate && startDate <= now);
};

export const isDateAfterDraftDate = (date: Date | null | undefined, lastDayOfDraft: Date | null): boolean => {
  if (!lastDayOfDraft || !date) {
    return false;
  }

  return date > lastDayOfDraft;
};

export const validateSchoolEndDateWithDraftDate = (school: ProspectSchoolDTO, lastDayOfDraft: Date | null): boolean => {
  const endDate = !!school.endDate ? new Date(school.endDate) : null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const previous = isPrevious(school);
  const future = isFuture(school);

  if (!endDate) {
    return false;
  }

  // if endDate is not a valid date, return false immediately
  if (isNaN(endDate.getTime())) {
    return false;
  }

  // for future schools, allow any future end date regardless of LastDayOfDraft
  if (future) {
    return true;
  }

  // if we're already past the LastDayOfDraft date, allow any future end date
  if (lastDayOfDraft !== null && now > lastDayOfDraft) {
    if (previous) {
      return endDate <= now;
    }
    // for current schools, allow any end date when past draft deadline
    return true;
  }

  // prevent end dates that are after the LastDayOfDraft date (only when current date is before draft deadline)
  if (lastDayOfDraft !== null && endDate > lastDayOfDraft) {
    return false;
  }

  if (previous) {
    return endDate <= now;
  }

  // for current schools, allow any end date that is before LastDayOfDraft
  return true;
};

export const isValidSchoolEndDate = (school: ProspectSchoolDTO, lastDayOfDraft: Date | null): boolean => {
  try {
    return validateSchoolEndDateWithDraftDate(school, lastDayOfDraft);
  } catch (error) {
    // if we can't load LastDayOfDraft, throw the error to be handled by the caller
    console.error("Failed to load LastDayOfDraft system setting:", error);
    throw error;
  }
};

export const validSchool = (
  school: ProspectSchoolDTO,
  isActingAsProspect: boolean,
  lastDayOfDraft: Date | null,
): boolean => {
  const isNoSchool = school.ebisSchoolId == 0;
  const startDate = !!school.startDate ? new Date(school.startDate) : null;
  const endDate = !!school.endDate ? new Date(school.endDate) : null;

  const mandatory =
    !!(
      school.schoolTypeId &&
      school.schoolName &&
      school.schoolState &&
      school.ebisSchoolId &&
      !!startDate &&
      !isNaN(startDate.getTime()) &&
      !!endDate &&
      !isNaN(endDate.getTime()) &&
      isBefore(startDate, endDate)
    ) || isNoSchool;

  const college: boolean = isCollege(school);
  const current: boolean = isCurrent(school);
  const academicClass: boolean = !current || !!school.academicClassId || isNoSchool;
  const classDifferent: boolean = !(college && current) || school.schoolClassDifferent != null;
  const shirted: boolean =
    isFuture(school) ||
    !college ||
    (school.hasShirted != null && (school.hasShirted === false || !!school.shirtedYears?.length));
  const ninth: boolean = !isHighSchool(school) || !current || school.ninthGradeYear != null;

  const isValidStartDate: boolean = validStartDate(school) || isActingAsProspect;
  const isValidEndDate: boolean = validateSchoolEndDateWithDraftDate(school, lastDayOfDraft) || isActingAsProspect;

  return mandatory && academicClass && classDifferent && shirted && ninth && isValidStartDate && isValidEndDate;
};

export const wentToCanadianHS = (current: ProspectSchoolDTO | undefined, previous: ProspectSchoolDTO[]): boolean =>
  (current?.schoolCountry === "Canada" && isHighSchool(current)) ||
  previous.some((school) => school.schoolCountry === "Canada" && isHighSchool(school));
